<!--
// app/components/pages/mercato/MercatoKeyConcepts.vue
-->
<script setup lang="ts">
import GlossaryDrawer from "~/components/glossary/GlossaryDrawer.vue";
import ConceptLink from "~/components/glossary/ConceptLink.vue";
import CloseIcon from "~/components/Shared/icons/CloseIcon.vue";
import BookIcon from "~/components/Shared/icons/BookIcon.vue";
import { isPastDate } from "~~/src/Shared/utils";

const { $t } = useI18n();

// Drawer visibility state
const isDrawerOpen = ref(false);

// Drawer ref for programmatic control
const drawerRef = ref<InstanceType<typeof GlossaryDrawer> | null>(null);

// Scroll detection for button minimization
const isScrolled = ref(false);

onMounted(() => {
  const handleScroll = () => {
    isScrolled.value = window.scrollY > 100;
  };

  const handleOpenGlossary = (event: Event) => {
    const customEvent = event as CustomEvent<{ conceptId: string }>;
    if (customEvent.detail?.conceptId) {
      openDrawer(customEvent.detail.conceptId);
    }
  };

  window.addEventListener("scroll", handleScroll);
  window.addEventListener("open-mercato-glossary", handleOpenGlossary);

  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
    window.removeEventListener("open-mercato-glossary", handleOpenGlossary);
  });
});

// Build categories from i18n
const categories = computed(() => {
  const marketMechanics = isPastDate("2026-02-06T02:00:00Z")
    ? ["marketValue", "releaseClause", "trade", "transfer", "freeAgent", "salaryCap"]
    : ["marketValue", "releaseClause", "trade", "transfer", "freeAgent"];

  const playerStatus = ["available", "inNegotiation", "newSigning", "secured", "locked", "newAcquisition"];

  const offerIndicators = ["playerDecision", "rofr", "negotiation"];

  return [
    {
      title: $t("mercato.keyConcepts.categories.marketMechanics.title"),
      concepts: marketMechanics.map(key => ({
        id: `concept-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
        title: $t(`mercato.keyConcepts.categories.marketMechanics.concepts.${key}.title`),
        definition: $t(`mercato.keyConcepts.categories.marketMechanics.concepts.${key}.definition`),
      })),
    },
    {
      title: $t("mercato.keyConcepts.categories.playerStatus.title"),
      concepts: playerStatus.map(key => ({
        id: `concept-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
        title: $t(`mercato.keyConcepts.categories.playerStatus.concepts.${key}.title`),
        definition: $t(`mercato.keyConcepts.categories.playerStatus.concepts.${key}.definition`),
      })),
    },
    {
      title: $t("mercato.keyConcepts.categories.offerIndicators.title"),
      concepts: offerIndicators.map(key => ({
        id: `concept-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
        title: $t(`mercato.keyConcepts.categories.offerIndicators.concepts.${key}.title`),
        definition: $t(`mercato.keyConcepts.categories.offerIndicators.concepts.${key}.definition`),
      })),
    },
  ];
});

// Open drawer and optionally scroll to concept
const openDrawer = (conceptId?: string) => {
  isDrawerOpen.value = true;

  if (conceptId && drawerRef.value) {
    nextTick(() => {
      drawerRef.value?.scrollToConcept(conceptId);
    });
  }
};

// Handle concept link clicks - no URL hash updates
const onConceptLinkClick = (conceptId: string) => {
  openDrawer(conceptId);
};

// Handle concept clicked in drawer - no URL hash updates
const onConceptClicked = (conceptId: string) => {
  // Just for tracking, no URL updates
};

// Expose ConceptLink component and openDrawer for use in parent
defineExpose({ ConceptLink, openDrawer });
</script>

<template>
  <div class="mercato-key-concepts">
    <!-- Floating toggle button -->
    <button
      type="button"
      :class="[
        'fixed flex items-center gap-2 shadow-lg transition-all duration-500 ease-in-out',
        isDrawerOpen
          ? 'z-[2001] top-4 ltr:right-4 rtl:left-4 ltr:sm:right-[420px] ltr:md:right-[440px] rtl:sm:left-[420px] rtl:md:left-[440px] px-3 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-full'
          : isScrolled
            ? 'z-[300] top-36 ltr:right-4 rtl:left-4 px-3 py-3 bg-mercato hover:bg-mercato-light text-black font-semibold rounded-lg'
            : 'z-[300] top-40 ltr:right-4 rtl:left-4 px-4 py-3 bg-mercato hover:bg-mercato/90 text-black font-semibold rounded-lg',
      ]"
      @click="isDrawerOpen ? (isDrawerOpen = false) : openDrawer()"
    >
      <!-- Close icon (when drawer is open) -->
      <CloseIcon v-if="isDrawerOpen" width="20" height="20" />

      <!-- Book icon (when drawer is closed) -->
      <template v-else>
        <BookIcon width="20" height="20" />

        <!-- Text with smooth fade transition -->
        <Transition name="fade-text">
          <span v-if="!isScrolled" class="hidden sm:inline whitespace-nowrap">{{
            $t("mercato.keyConcepts.openButton")
          }}</span>
        </Transition>
      </template>
    </button>

    <!-- Drawer -->
    <glossary-drawer
      ref="drawerRef"
      v-model="isDrawerOpen"
      :categories="categories"
      :search-placeholder="$t('mercato.keyConcepts.search')"
      :pin-label="$t('mercato.keyConcepts.pin')"
      :unpin-label="$t('mercato.keyConcepts.unpin')"
      @concept-clicked="onConceptClicked"
    >
      <template #title>
        {{ $t("mercato.keyConcepts.title") }}
      </template>
    </glossary-drawer>

    <!-- Provide ConceptLink component for use in parent -->
    <slot :open-drawer="openDrawer" :on-concept-link-click="onConceptLinkClick"></slot>
  </div>
</template>

<style scoped>
/* Smooth fade transition for button text */
.fade-text-enter-active,
.fade-text-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-text-enter-from,
.fade-text-leave-to {
  opacity: 0;
}
</style>
